// src/utils/relativeTime.js
//
// Helper sin dependencias externas para mostrar fechas en formato relativo
// ("hace 2 horas", "ayer", "en 3 días"). Si la fecha es de hace más de
// 30 días, devuelve la fecha absoluta corta ("12 mar 2026").

const RTF = typeof Intl !== 'undefined' && Intl.RelativeTimeFormat
    ? new Intl.RelativeTimeFormat('es', { numeric: 'auto' })
    : null;

const ABS_FMT = typeof Intl !== 'undefined' && Intl.DateTimeFormat
    ? new Intl.DateTimeFormat('es', { day: 'numeric', month: 'short', year: 'numeric' })
    : null;

const UNITS = [
    { unit: 'year',   seconds: 31536000 },
    { unit: 'month',  seconds: 2592000  },
    { unit: 'week',   seconds: 604800   },
    { unit: 'day',    seconds: 86400    },
    { unit: 'hour',   seconds: 3600     },
    { unit: 'minute', seconds: 60       },
    { unit: 'second', seconds: 1        },
];

/**
 * Devuelve "hace X" o "en X". Si pasa de 30 días, devuelve fecha corta.
 * @param {string|Date} value
 * @returns {string}
 */
export function relativeTime(value) {
    if (!value) return '';
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return '';

    const diffSec = (date.getTime() - Date.now()) / 1000;
    const absSec = Math.abs(diffSec);

    // Más de 30 días → fecha absoluta
    if (absSec > 30 * 86400) {
        return ABS_FMT ? ABS_FMT.format(date) : date.toLocaleDateString('es');
    }

    // Menos de 1 minuto → "ahora"
    if (absSec < 60) return 'ahora';

    if (!RTF) return date.toLocaleString('es');

    for (const { unit, seconds } of UNITS) {
        if (absSec >= seconds) {
            const value = Math.round(diffSec / seconds);
            return RTF.format(value, unit);
        }
    }
    return 'ahora';
}

/** Formato absoluto siempre (para tooltip). */
export function absoluteTime(value) {
    if (!value) return '';
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return '';
    return date.toLocaleString('es');
}

const DT_FMT = typeof Intl !== 'undefined' && Intl.DateTimeFormat
    ? new Intl.DateTimeFormat('es', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: false,
    })
    : null;

/** Fecha + hora absolutas, formato corto legible: "28 nov 2026, 14:30".
 *  Para mostrar en la UI cuando se prefiere fecha/hora explícita sobre el
 *  relativo ("hace 3 días"). */
export function dateTime(value) {
    if (!value) return '';
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return '';
    return DT_FMT ? DT_FMT.format(date) : date.toLocaleString('es');
}
